import { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import JsonLd from '@/components/feature/JsonLd';
import { comics, categories } from '@/mocks/comics';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'popular' | 'latest' | 'rating'>('popular');

  const categoryName = useMemo(() => {
    const cat = categories.find((c) => c.slug === slug);
    return cat ? cat.name : '全部';
  }, [slug]);

  const filteredComics = useMemo(() => {
    let result = slug === 'all' ? [...comics] : comics.filter((c) => c.categorySlug === slug);
    if (sortBy === 'popular') {
      result.sort((a, b) => b.views - a.views);
    } else if (sortBy === 'latest') {
      result.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
    } else if (sortBy === 'rating') {
      result.sort((a, b) => b.rating - a.rating);
    }
    return result;
  }, [slug, sortBy]);

  const collectionJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName}韩漫 - 韩漫画网`,
    description: `韩漫画网${categoryName}分类，提供下拉式完整版韩漫免费阅读和无删减漫画免费阅读。`,
    url: `https://www.hanmanhua.com/category/${slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredComics.map((comic, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Book',
          name: comic.title,
          author: { '@type': 'Person', name: comic.author },
          url: `https://www.hanmanhua.com/comic/${comic.id}`,
        },
      })),
    },
  };

  return (
    <>
      <JsonLd data={collectionJsonLd} />
      <main className="pt-20 pb-12">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <div className="mb-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: `${categoryName}韩漫` },
              ]}
            />
          </div>

          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                {categoryName}韩漫
              </h1>
              <p className="mt-2 text-sm text-foreground-600">
                共 {filteredComics.length} 部漫画 · 下拉式完整版韩漫免费阅读
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4 mb-8 overflow-x-auto pb-2">
            {categories.map((cat) => (
              <a
                key={cat.slug}
                href={`/category/${cat.slug}`}
                className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  slug === cat.slug || (!slug && cat.slug === 'all')
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-2 mb-6">
            <span className="text-sm text-foreground-500 mr-1">排序：</span>
            {[
              { key: 'popular' as const, label: '最受欢迎' },
              { key: 'latest' as const, label: '最近更新' },
              { key: 'rating' as const, label: '最高评分' },
            ].map((opt) => (
              <button
                key={opt.key}
                onClick={() => setSortBy(opt.key)}
                className={`px-4 py-1.5 text-sm rounded-lg transition-colors whitespace-nowrap ${
                  sortBy === opt.key
                    ? 'bg-primary-100 text-primary-700 font-medium'
                    : 'text-foreground-600 hover:bg-background-100'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {filteredComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5">
              {filteredComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                <i className="ri-book-open-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">暂无该分类的漫画</p>
            </div>
          )}
        </div>
      </main>
    </>
  );
}